/**
 * VolumeControl: the round speaker button in the entry and lobby headers, and its slider popover.
 *
 * Lifted from Ringmaster's `ui/VolumeControl.tsx`, which is the same shape: one button, one popover
 * with mute + a master slider + the number. What differs is the plumbing. Ringmaster drove its
 * player directly; here the button writes `settings.volume.master` and `settings.audioMuted` and
 * nothing else, and the audio layer follows the store (`installMusic` pushes both into the engine
 * on every change, on every screen). The Settings sheet's Master slider is the same value, so the
 * two never disagree.
 *
 * Mute is remembered separately from the level so unmuting restores where the slider was.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useStore } from '../../store/store';

export function VolumeControl({ className }: { className?: string } = {}) {
  const [open, setOpen] = useState(false);
  const muted = useStore((s) => s.settings.audioMuted);
  const volume = useStore((s) => s.settings.volume.master);
  const setVolume = useStore((s) => s.setVolume);
  const setSettings = useStore((s) => s.setSettings);
  const rootRef = useRef<HTMLDivElement>(null);

  // Outside click / Escape close. The listeners exist only while the popover is open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const silent = muted || volume <= 0;
  const toggleMute = () => setSettings({ audioMuted: !muted });
  const sliderId = useId();

  return (
    <div ref={rootRef} className={['volume', className].filter(Boolean).join(' ')} data-testid="volume">
      <button
        type="button"
        className="volume__btn"
        aria-label={silent ? 'Sound off' : 'Volume'}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="volume-toggle"
        data-muted={silent ? '' : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <SpeakerIcon level={silent ? 0 : volume} />
      </button>
      {open && (
        <div className="volume__pop" role="dialog" aria-label="Volume" data-testid="volume-pop">
          <button
            type="button"
            className="volume__btn volume__btn--sm"
            aria-label={muted ? 'Unmute' : 'Mute'}
            aria-pressed={muted}
            data-testid="volume-mute"
            onClick={toggleMute}
          >
            <SpeakerIcon level={muted ? 0 : volume} />
          </button>
          <div className="slider volume__slider">
            <input
              id={sliderId}
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              disabled={muted}
              aria-label="Master volume"
              data-testid="volume-slider"
              onChange={(e) => setVolume({ master: Number(e.target.value) })}
            />
          </div>
          <span className="volume__num tnum" aria-hidden>
            {muted ? '—' : Math.round(volume * 100)}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Drawn, like the lobby's cog: 🔊 is a colour emoji in every font the UI uses. The arcs come and
 * go with the level so the closed button says how loud things are, not only whether they are on.
 */
function SpeakerIcon({ level }: { level: number }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 9.5v5h3.2L12 18.5v-13L7.2 9.5z" fill="currentColor" stroke="none" />
      {level <= 0 ? (
        <path d="M16 9.5l5 5M21 9.5l-5 5" />
      ) : (
        <>
          <path d="M15.5 9.6a3.4 3.4 0 0 1 0 4.8" />
          {level > 0.5 && <path d="M18.3 7a7.2 7.2 0 0 1 0 10" />}
        </>
      )}
    </svg>
  );
}
